import type { AxiosProgressEvent } from 'axios'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import LidarProgressIndicator from '../Components/Layers/modals/LidarProgressIndicator'
import {
  buildDjangoHttpBase,
  buildWsUrl,
  mapLidarProcessingPercent,
} from '../Components/Layers/modals/lidarModalUtils'
import { uploadRaster } from '../api/endpoints'

export type LidarLogEntry = {
  id: string
  message: string
  stage: string
  percent: number | null
  ts: number
}

export type LidarProgressState = {
  active: boolean
  percent: number
  status: string
  stage: string
  detail: string
  fileName: string
  clientId: string | null
  uploadId: string | null
  logs: LidarLogEntry[]
  isError: boolean
}

export type LidarUploadQueueItem = {
  formData: FormData
  clientId: string
  uploadId: string
  fileName: string
  onUploadSuccess?: () => void
}

export type LidarIndicatorPosition = { x: number; y: number }

export type LidarUploadContextValue = {
  progressState: LidarProgressState
  uploadQueue: LidarUploadQueueItem[]
  isUploading: boolean
  effectiveUploading: boolean
  isQueueFull: boolean
  maxQueue: number
  isIndicatorHidden: boolean
  position: LidarIndicatorPosition
  setPosition: (
    next:
      | LidarIndicatorPosition
      | ((prev: LidarIndicatorPosition) => LidarIndicatorPosition),
  ) => void
  enqueueUpload: (payload: LidarUploadQueueItem) => boolean
  removeFromQueue: (uploadId: string) => void
  cancelCurrent: () => Promise<void>
  dismissIndicator: () => void
}

type LidarWsPayload = {
  progress?: number
  status?: string
  stage?: string
  detail?: string
}

type LidarSnapshot = {
  clientId: string
  uploadId: string
  fileName: string
  percent: number
  status: string
  stage: string
  detail: string
  isError: boolean
  logs: LidarLogEntry[]
}

const STORAGE_KEY = 'elda:lidar:activeUpload'
const POSITION_KEY = 'elda:lidar:indicatorPosition'
const MAX_LOGS = 4
const PERSIST_LOGS = 3
const MAX_QUEUE = 3

// throttle window for writing the progress snapshot to localStorage
const PERSIST_THROTTLE_MS = 250
const DONE_HIDE_DELAY_MS = 1800
const INDICATOR_WIDTH = 260

const INITIAL_PROGRESS: LidarProgressState = Object.freeze({
  active: false,
  percent: 0,
  status: '',
  stage: '',
  detail: '',
  fileName: '',
  clientId: null,
  uploadId: null,
  logs: [] as LidarLogEntry[],
  isError: false,
}) as LidarProgressState

const readStorage = <T,>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch (err) {
    console.warn(`[lidar] storage read failed for ${key}`, err)
    return null
  }
}

const writeStorage = (key: string, value: unknown): void => {
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (err) {
    console.warn(`[lidar] storage write failed for ${key}`, err)
  }
}

const computeDefaultPosition = (): LidarIndicatorPosition => ({
  x: Math.max(12, (window.innerWidth - INDICATOR_WIDTH) / 2),
  y: 16,
})

const generateId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

const detectError = (status: string, stage: string): boolean => {
  if (stage === 'error') return true
  const lower = status.toLowerCase()
  return lower.includes('error') || lower.includes('erreur')
}

const isValidSnapshot = (raw: unknown): raw is LidarSnapshot => {
  if (!raw || typeof raw !== 'object') return false
  const obj = raw as Partial<LidarSnapshot>
  return Boolean(obj.uploadId && obj.clientId)
}

// reducer actions
type ProgressAction =
  | {
      type: 'start'
      clientId: string
      uploadId: string
      fileName: string
    }
  | { type: 'http-progress'; percent: number }
  | { type: 'ws-message'; payload: LidarWsPayload }
  | { type: 'http-error'; detail: string }
  | { type: 'cancelled' }
  | { type: 'reset' }
  | { type: 'rehydrate'; snapshot: LidarSnapshot }

const makeLog = (
  message: string,
  stage: string,
  percent: number | null,
): LidarLogEntry => ({
  id: generateId(),
  message,
  stage,
  percent,
  ts: Date.now(),
})

const prependLog = (
  logs: LidarLogEntry[],
  entry: LidarLogEntry,
): LidarLogEntry[] => [entry, ...logs].slice(0, MAX_LOGS)

const progressReducer = (
  state: LidarProgressState,
  action: ProgressAction,
): LidarProgressState => {
  switch (action.type) {
    case 'start': {
      const status = 'Téléchargement du fichier…'
      return {
        active: true,
        percent: 0,
        status,
        stage: 'upload',
        detail: '',
        fileName: action.fileName,
        clientId: action.clientId,
        uploadId: action.uploadId,
        logs: [makeLog(status, 'upload', 0)],
        isError: false,
      }
    }
    case 'http-progress': {
      const scaled = Math.min(25, Math.round(action.percent * 0.25))
      if (scaled <= state.percent) return state
      return {
        ...state,
        active: true,
        percent: scaled,
        status: 'Téléchargement du fichier…',
        stage: 'upload',
      }
    }
    case 'ws-message': {
      const { progress, status = '', stage = '', detail = '' } = action.payload
      const uiPercent =
        typeof progress === 'number'
          ? mapLidarProcessingPercent(progress)
          : null
      const hasError = detectError(status, stage)
      const isDone = !hasError && (stage === 'done' || (progress ?? 0) >= 100)
      const normalizedStatus = isDone ? 'Téléchargement terminé' : status

      const nextLogs = normalizedStatus
        ? prependLog(
            state.logs,
            makeLog(normalizedStatus, stage, progress ?? null),
          )
        : state.logs

      return {
        ...state,
        active: true,
        percent:
          uiPercent !== null
            ? Math.max(state.percent, uiPercent)
            : state.percent,
        status: hasError
          ? 'Erreur pendant l’import'
          : normalizedStatus || state.status,
        stage: stage || state.stage,
        detail: detail || state.detail,
        isError: hasError || state.isError,
        logs: nextLogs,
      }
    }
    case 'http-error':
      return {
        ...state,
        active: true,
        isError: true,
        status: 'Erreur pendant l’import',
        stage: 'error',
        detail: action.detail,
      }
    case 'cancelled':
      return {
        ...state,
        active: true,
        isError: true,
        status: 'Import annulé',
        stage: 'cancelled',
        detail: 'Annulation demandée',
      }
    case 'rehydrate':
      return {
        active: true,
        percent: action.snapshot.percent ?? 0,
        status: action.snapshot.status || 'Reprise du suivi…',
        stage: action.snapshot.stage || '',
        detail: action.snapshot.detail || '',
        fileName: action.snapshot.fileName || '',
        clientId: action.snapshot.clientId,
        uploadId: action.snapshot.uploadId,
        logs: Array.isArray(action.snapshot.logs) ? action.snapshot.logs : [],
        isError: false,
      }
    case 'reset':
      return INITIAL_PROGRESS
    default:
      return state
  }
}

export const LidarUploadContext = createContext<LidarUploadContextValue | null>(
  null,
)

type LidarUploadProviderProps = { children: ReactNode }

export const LidarUploadProvider = ({ children }: LidarUploadProviderProps) => {
  const [progressState, dispatch] = useReducer(
    progressReducer,
    INITIAL_PROGRESS,
  )
  const [uploadQueue, setUploadQueue] = useState<LidarUploadQueueItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isIndicatorHidden, setIsIndicatorHidden] = useState(false)
  const [position, setPositionState] = useState<LidarIndicatorPosition>(() => {
    const stored = readStorage<LidarIndicatorPosition>(POSITION_KEY)
    if (stored && Number.isFinite(stored.x) && Number.isFinite(stored.y)) {
      return stored
    }
    return computeDefaultPosition()
  })

  const wsRef = useRef<WebSocket | null>(null)
  const cancelControllerRef = useRef<AbortController | null>(null)
  const currentUploadRef = useRef<{
    clientId: string
    uploadId: string
  } | null>(null)
  const isUserCancelRef = useRef(false)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPersistRef = useRef(0)
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onSuccessCallbackRef = useRef<(() => void) | null>(null)

  // persistence
  const clearPersistence = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    lastPersistRef.current = 0
    writeStorage(STORAGE_KEY, null)
  }, [])

  useEffect(() => {
    const {
      active,
      uploadId,
      clientId,
      isError,
      stage,
      fileName,
      percent,
      status,
      detail,
      logs,
    } = progressState

    const isHttpPhase = stage === 'upload' || stage === ''
    if (
      !active ||
      !uploadId ||
      !clientId ||
      isError ||
      stage === 'done' ||
      isHttpPhase
    ) {
      return undefined
    }

    const snapshot: LidarSnapshot = {
      clientId,
      uploadId,
      fileName,
      percent,
      status,
      stage,
      detail,
      isError,
      logs: logs.slice(0, PERSIST_LOGS),
    }

    // Throttle leading-edge: write immediately if enough time elapsed since
    // last write, otherwise schedule a trailing write so the latest snapshot
    // is always flushed.
    const now = Date.now()
    const elapsed = now - lastPersistRef.current
    if (elapsed >= PERSIST_THROTTLE_MS) {
      lastPersistRef.current = now
      writeStorage(STORAGE_KEY, snapshot)
      return undefined
    }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
    persistTimerRef.current = setTimeout(() => {
      lastPersistRef.current = Date.now()
      writeStorage(STORAGE_KEY, snapshot)
      persistTimerRef.current = null
    }, PERSIST_THROTTLE_MS - elapsed)

    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [progressState])

  // warn before unload while the browser is still streaming the file
  useEffect(() => {
    if (progressState.stage !== 'upload' || !progressState.active) {
      return undefined
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [progressState.stage, progressState.active])

  // web socket lifecycle
  const closeSocket = useCallback(() => {
    const socket = wsRef.current
    if (!socket) return
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
    socket.onopen = null
    socket.close()
    wsRef.current = null
  }, [])

  const scheduleResetTimer = useCallback(() => {
    if (doneTimerRef.current) clearTimeout(doneTimerRef.current)
    doneTimerRef.current = setTimeout(() => {
      dispatch({ type: 'reset' })
      setIsIndicatorHidden(false)
      doneTimerRef.current = null
    }, DONE_HIDE_DELAY_MS)
  }, [])

  // advance the upload queue. this is useful to reset the state and hide the indicator when the upload is done or when there is an error.
  const advanceQueue = useCallback(
    (options: { hadError?: boolean } = {}) => {
      closeSocket()
      currentUploadRef.current = null
      cancelControllerRef.current = null
      onSuccessCallbackRef.current = null
      isUserCancelRef.current = false
      setIsUploading(false)
      setUploadQueue(prev => prev.slice(1))
      if (!options.hadError) {
        scheduleResetTimer()
      }
    },
    [closeSocket, scheduleResetTimer],
  )

  const connectProgressSocket = useCallback(
    ({ clientId, uploadId }: { clientId: string; uploadId: string }) => {
      if (!clientId || !uploadId) return
      closeSocket()

      const wsUrl = buildWsUrl({
        baseUrl: import.meta.env.VITE_URL || window.location.origin,
        clientId,
        uploadId,
      })
      if (!wsUrl) {
        console.warn('[lidar-ws] missing url')
        return
      }

      const socket = new WebSocket(wsUrl)
      wsRef.current = socket

      socket.onmessage = (event: MessageEvent<string>) => {
        let payload: LidarWsPayload
        try {
          payload = JSON.parse(event.data) as LidarWsPayload
        } catch (err) {
          console.error('[lidar-ws] invalid payload', err)
          return
        }

        const hasError = detectError(payload.status || '', payload.stage || '')
        const isDone =
          !hasError &&
          (payload.stage === 'done' || (payload.progress ?? 0) >= 100)

        dispatch({ type: 'ws-message', payload })

        if (isDone) {
          window.dispatchEvent(new CustomEvent('lidar-upload-success'))
          clearPersistence()
          advanceQueue({ hadError: false })
        } else if (hasError) {
          clearPersistence()
          advanceQueue({ hadError: true })
        }
      }

      socket.onerror = () => {
        console.warn('[lidar-ws] socket error')
      }

      socket.onclose = () => {
        if (wsRef.current === socket) wsRef.current = null
      }
    },
    [advanceQueue, clearPersistence, closeSocket],
  )

  // hydrate on mount: reconnect WS only if the *backend* was already processing
  // (stage !== 'upload'). A snapshot stuck in the HTTP phase is stale by definition since the browser aborted the upload on unload.
  useEffect(() => {
    const snapshot = readStorage<LidarSnapshot>(STORAGE_KEY)
    if (!isValidSnapshot(snapshot)) return
    const isResumable =
      snapshot.stage &&
      snapshot.stage !== 'upload' &&
      snapshot.stage !== 'done' &&
      !snapshot.isError
    if (!isResumable) {
      writeStorage(STORAGE_KEY, null)
      return
    }
    dispatch({ type: 'rehydrate', snapshot })
    setIsIndicatorHidden(false)
    connectProgressSocket({
      clientId: snapshot.clientId,
      uploadId: snapshot.uploadId,
    })
  }, [connectProgressSocket])

  // cleanup on full unmount
  useEffect(() => {
    return () => {
      closeSocket()
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current)
    }
  }, [closeSocket])

  const setPosition = useCallback<LidarUploadContextValue['setPosition']>(
    next => {
      setPositionState(prev => {
        const value = typeof next === 'function' ? next(prev) : next
        writeStorage(POSITION_KEY, value)
        return value
      })
    },
    [],
  )

  // upload orchestration
  const startUpload = useCallback(
    async (payload: LidarUploadQueueItem) => {
      if (doneTimerRef.current) {
        clearTimeout(doneTimerRef.current)
        doneTimerRef.current = null
      }
      setIsUploading(true)
      const { clientId, uploadId, fileName, formData, onUploadSuccess } =
        payload
      currentUploadRef.current = { clientId, uploadId }
      onSuccessCallbackRef.current = onUploadSuccess ?? null
      isUserCancelRef.current = false

      dispatch({ type: 'start', clientId, uploadId, fileName })
      setIsIndicatorHidden(false)
      connectProgressSocket({ clientId, uploadId })

      const controller = new AbortController()
      cancelControllerRef.current = controller

      try {
        await uploadRaster(formData, {
          signal: controller.signal,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (!event.total) return
            const percent = Math.round((event.loaded / event.total) * 100)
            dispatch({ type: 'http-progress', percent })
          },
        })
        try {
          onSuccessCallbackRef.current?.()
        } catch (cbErr) {
          console.error('[lidar] onUploadSuccess callback failed', cbErr)
        }
      } catch (err) {
        const error = err as {
          name?: string
          code?: string
          response?: { data?: { error?: string; message?: string } }
          message?: string
        }
        const isCancel =
          error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED'
        if (isCancel) {
          if (!isUserCancelRef.current) {
            dispatch({ type: 'cancelled' })
            clearPersistence()
            advanceQueue({ hadError: true })
          }
        } else {
          console.error('[lidar] upload error', err)
          const detail =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Erreur inconnue'
          dispatch({ type: 'http-error', detail })
          clearPersistence()
          advanceQueue({ hadError: true })
        }
      }
    },
    [advanceQueue, clearPersistence, connectProgressSocket],
  )

  useEffect(() => {
    if (!isUploading && uploadQueue.length > 0) {
      void startUpload(uploadQueue[0])
    }
  }, [isUploading, uploadQueue, startUpload])

  const enqueueUpload = useCallback<LidarUploadContextValue['enqueueUpload']>(
    payload => {
      let accepted = false
      setUploadQueue(prev => {
        if (prev.length >= MAX_QUEUE) return prev
        accepted = true
        return [...prev, payload]
      })
      return accepted
    },
    [],
  )

  // Remove a *pending* item from the queue. The currently uploading item
  // (index 0) is protected and must be aborted via `cancelCurrent`.
  const removeFromQueue = useCallback<
    LidarUploadContextValue['removeFromQueue']
  >(uploadId => {
    if (!uploadId) return
    setUploadQueue(prev => {
      if (prev.length <= 1) return prev
      const [head, ...rest] = prev
      const filtered = rest.filter(item => item.uploadId !== uploadId)
      if (filtered.length === rest.length) return prev
      return [head, ...filtered]
    })
  }, [])

  const cancelCurrent = useCallback(async () => {
    const current = currentUploadRef.current
    if (!current?.uploadId) return
    isUserCancelRef.current = true

    cancelControllerRef.current?.abort()
    closeSocket()

    setIsUploading(false)
    currentUploadRef.current = null
    cancelControllerRef.current = null
    setUploadQueue([])
    dispatch({ type: 'reset' })
    setIsIndicatorHidden(false)
    clearPersistence()

    const djangoBase = buildDjangoHttpBase({
      djangoUrl: import.meta.env.VITE_DJANGO_URL,
      djangoWsUrl: import.meta.env.VITE_DJANGO_WS_URL,
    })
    if (!djangoBase) return

    await fetch(`${djangoBase}/traitementlidar/process-cancel/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        upload_id: current.uploadId,
        client_id: current.clientId,
      }),
    }).catch(err => {
      console.error('[lidar] cancel request failed', err)
    })
  }, [clearPersistence, closeSocket])

  const dismissIndicator = useCallback(() => {
    setIsIndicatorHidden(true)
  }, [])

  // derived values
  const isQueueFull = uploadQueue.length >= MAX_QUEUE
  const effectiveUploading =
    isUploading ||
    (progressState.active &&
      progressState.stage !== 'done' &&
      !progressState.isError)

  const value = useMemo<LidarUploadContextValue>(
    () => ({
      progressState,
      uploadQueue,
      isUploading,
      effectiveUploading,
      isQueueFull,
      maxQueue: MAX_QUEUE,
      isIndicatorHidden,
      position,
      setPosition,
      enqueueUpload,
      removeFromQueue,
      cancelCurrent,
      dismissIndicator,
    }),
    [
      progressState,
      uploadQueue,
      isUploading,
      effectiveUploading,
      isQueueFull,
      isIndicatorHidden,
      position,
      setPosition,
      enqueueUpload,
      removeFromQueue,
      cancelCurrent,
      dismissIndicator,
    ],
  )

  return (
    <LidarUploadContext.Provider value={value}>
      {children}
      <LidarProgressIndicator
        active={progressState.active && !isIndicatorHidden}
        progressState={progressState}
        onClose={dismissIndicator}
        position={position}
        setPosition={setPosition}
        queue={uploadQueue}
      />
    </LidarUploadContext.Provider>
  )
}
